// Service layer reportes nutricionales (Sprint 6 Lote 2 T6).
// Tipos espejo de backend/app/schemas/reportes_nutri.py. Si el contrato
// del backend cambia, ajustar aqui — los componentes consumen estas
// interfaces, no la API directa.
import { api } from '../lib/api'

// ============================================================
// Tipos
// ============================================================
export interface IngestaPacienteRow {
  paciente_id: number
  paciente_nombre: string
  habitacion: string
  dieta_codigo: string
  dieta_nombre: string
  lineas_indicadas: number
  lineas_servidas: number
  lineas_excluidas: number
  comidas_total: number
}

export interface AdherenciaPacienteResponse {
  paciente_id: number
  paciente_nombre: string | null
  semana_inicio: string // ISO date YYYY-MM-DD
  semana_fin: string
  lineas_total: number
  lineas_servidas: number
  lineas_excluidas: number
  adherencia_pct: number
  comidas_total: number
}

export interface AlergiaPeriodoItem {
  id: number
  paciente_id: number
  paciente_nombre: string
  habitacion: string
  tipo_alimento: string
  alimento_id: number
  grupo_alimentario: string | null
  tipo_restriccion: string
  severidad: string | null
  fuente: string
  registrado_at: string // ISO datetime
}

export interface AlergiasPeriodoResponse {
  totales_por_fuente: Record<string, number>
  severidades: Record<string, number>
  total_items: number
  items: AlergiaPeriodoItem[]
}

export interface SustitucionPeriodoItem {
  id: number
  plato_id_origen: number
  plato_origen_codigo: string
  plato_origen_nombre: string
  plato_id_sustituto: number
  plato_sustituto_codigo: string
  plato_sustituto_nombre: string
  motivo_tipificado: string
  score_equivalencia: number // Decimal serializado como number
  activo: boolean
  creado_at: string
  creado_por_usuario_id: number
}

export interface SustitucionesPeriodoResponse {
  totales_por_motivo: Record<string, number>
  total_items: number
  items: SustitucionPeriodoItem[]
}

export type ReporteKind = 'ingesta' | 'adherencia' | 'alergias' | 'sustituciones'

// ============================================================
// API client
// ============================================================
export const reportesNutriApi = {
  ingestaSemanal: (params: {
    sede_id?: number | null
    semana_inicio: string
  }): Promise<IngestaPacienteRow[]> =>
    api
      .get('/nutricionista/reportes/ingesta-semanal', { params })
      .then((r) => r.data),

  adherencia: (
    paciente_id: number,
    params: { semana_inicio: string },
  ): Promise<AdherenciaPacienteResponse> =>
    api
      .get(`/nutricionista/reportes/adherencia/${paciente_id}`, { params })
      .then((r) => r.data),

  alergias: (params: {
    sede_id?: number | null
    fecha_inicio: string
    fecha_fin: string
  }): Promise<AlergiasPeriodoResponse> =>
    api
      .get('/nutricionista/reportes/alergias', { params })
      .then((r) => r.data),

  sustituciones: (params: {
    sede_id?: number | null
    fecha_inicio: string
    fecha_fin: string
  }): Promise<SustitucionesPeriodoResponse> =>
    api
      .get('/nutricionista/reportes/sustituciones', { params })
      .then((r) => r.data),

  exportCsv: (params: {
    kind: ReporteKind
    sede_id?: number | null
    semana_inicio?: string | null
    paciente_id?: number | null
    fecha_inicio?: string | null
    fecha_fin?: string | null
  }): Promise<{ filename: string; blob: Blob }> =>
    api
      .get('/nutricionista/reportes/export', {
        params: { tipo: 'csv', ...params },
        responseType: 'blob',
      })
      .then((r) => {
        // Extrae filename del Content-Disposition cuando exista; fallback
        // genera nombre cliente con kind + fecha local.
        const dispo = String(r.headers?.['content-disposition'] ?? '')
        const match = /filename="?([^";]+)"?/i.exec(dispo)
        const filename =
          match?.[1] ??
          `reporte_${params.kind}_${new Date().toISOString().slice(0, 10)}.csv`
        return { filename, blob: r.data as Blob }
      }),
}
